import type { ReactNode } from 'react';
import { ClipboardPaste, Pin, Trash2 } from 'lucide-react';
import { Input } from '@/app/components/ui/input';
import { Checkbox } from '@/app/components/ui/checkbox';
import { TabsContent } from '@/app/components/ui/tabs';
import { CharBar } from '../CharBar';
import { addVariant, removeVariant, updateVariant } from '../useTemplateEditor';
import type { EditorReady } from './types';

type Props = {
  ed: EditorReady;
  adBar: ReactNode;
  fieldPalette: ReactNode;
  liveAdPreviewMini: ReactNode;
  filledHeadlines: number;
};

export function HeadlinesTab({ ed, adBar, fieldPalette, liveAdPreviewMini, filledHeadlines }: Props) {
  const {
    templateSet, activeHeadlines, isMultiAd, activeAdIndex,
    expandedVariants, setExpandedVariants, allVariantsExpanded, setAllVariantsExpanded,
    updateAd, updateTemplateContent,
    updateActiveHeadlineSlot, updateActiveHeadlineSlotProp,
    handleActiveSlotPaste, pasteActiveHeadlines,
    inputProps, getPreviewText, getCharInfo,
  } = ed;

  return (
    <TabsContent value="headlines">
      {liveAdPreviewMini}
      {adBar}
      {fieldPalette}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="grid gap-3 px-3 py-1.5 bg-muted/40 border-b border-border text-[10px] font-mono text-muted-foreground uppercase tracking-widest items-center"
          style={{ gridTemplateColumns: '36px 28px 1.5fr 1fr 72px 28px' }}>
          <span>#</span>
          <span title="Pin to position"><Pin className="size-2.5 opacity-40" /></span>
          <span className="flex items-center gap-2">
            Template
            <button
              className="text-[9px] font-mono text-muted-foreground/40 hover:text-primary border border-border/40 rounded px-1 py-0.5 transition-colors normal-case tracking-normal"
              title="Capitalize first letter of each word in all headlines"
              onClick={() => {
                const titleCase = (text: string) => {
                  const parts = text.split(/(\{[^}]*\})/);
                  return parts.map((part, i) => i % 2 === 1 ? part : part.replace(/\b[a-z]/g, c => c.toUpperCase())).join('');
                };
                const updated = activeHeadlines.map(slot => ({
                  ...slot,
                  variants: slot.variants.map(v => ({ ...v, template_string: titleCase(v.template_string) })),
                }));
                if (isMultiAd || templateSet.template_content.ads) {
                  updateAd(activeAdIndex, { headlines: updated });
                } else {
                  updateTemplateContent('headlines', updated);
                }
              }}
            >Aa</button>
            <button
              className="text-[11px] font-mono text-muted-foreground/70 hover:text-primary border border-border/50 hover:border-primary/40 hover:bg-primary/5 rounded px-2 py-0.5 transition-colors normal-case tracking-normal flex items-center gap-1.5"
              title="Paste headlines from clipboard (one per line)"
              onClick={() => pasteActiveHeadlines()}
            ><ClipboardPaste className="size-3" />Paste</button>
          </span>
          <span className="flex items-center gap-2">
            Preview
            <button
              className="text-[9px] font-mono text-muted-foreground/40 hover:text-primary border border-border/40 rounded px-1 py-0.5 transition-colors normal-case tracking-normal"
              onClick={() => {
                const allKeys: Record<string, boolean> = {};
                activeHeadlines.forEach(s => { allKeys[`h:${s.slot_number}`] = !allVariantsExpanded; });
                setExpandedVariants(prev => ({ ...prev, ...allKeys }));
                setAllVariantsExpanded(!allVariantsExpanded);
              }}
            >{allVariantsExpanded ? '− collapse all' : '+ expand all'}</button>
          </span>
          <span>Chars</span><span />
        </div>
        {activeHeadlines.map((slot) => {
          const primary = slot.variants[0] ?? { variant_order: 1, template_string: '', enabled: true };
          const extras = slot.variants.slice(1);
          const isExpanded = expandedVariants[`h:${slot.slot_number}`] ?? false;
          const hasContent = !!primary.template_string.trim();
          return (
            <div key={slot.slot_number} className="border-b border-border/40 last:border-0">
              <div
                className={`grid gap-3 px-3 py-1.5 items-center group hover:bg-muted/20 transition-colors ${!hasContent ? 'opacity-50' : ''}`}
                style={{ gridTemplateColumns: '36px 28px 1.5fr 1fr 72px 28px' }}
              >
                <span className="text-[11px] font-mono text-muted-foreground">H{slot.slot_number}</span>
                <button
                  className={`size-5 flex items-center justify-center rounded text-[9px] font-mono transition-colors ${
                    slot.pinned_position
                      ? 'bg-primary/15 text-primary border border-primary/30'
                      : 'text-muted-foreground/20 hover:text-muted-foreground/50'
                  }`}
                  title={slot.pinned_position ? `Pinned to position ${slot.pinned_position} — click to change` : 'Pin to position'}
                  onClick={() => {
                    const next = slot.pinned_position === undefined ? 1 : slot.pinned_position >= 3 ? undefined : (slot.pinned_position + 1) as 1 | 2 | 3;
                    updateActiveHeadlineSlotProp(slot.slot_number, { pinned_position: next });
                  }}
                >
                  {slot.pinned_position ? slot.pinned_position : <Pin className="size-2.5" />}
                </button>
                <Input
                  value={primary.template_string}
                  onChange={e => updateActiveHeadlineSlot(slot.slot_number, updateVariant(slot.variants, 1, { template_string: e.target.value }))}
                  onPaste={e => handleActiveSlotPaste(e, 'headline', slot.slot_number)}
                  placeholder={`Headline ${slot.slot_number}…`}
                  className="h-7 text-sm border-0 border-b border-border/40 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0"
                  {...inputProps(`h:${slot.slot_number}:1`)}
                />
                <div className="text-xs text-primary/70" title={getPreviewText(primary.template_string)}>
                  {getPreviewText(primary.template_string) || <span className="text-muted-foreground/30 italic">—</span>}
                </div>
                <CharBar {...getCharInfo(primary.template_string, 30)} limit={30} />
                <button
                  className="size-7 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    const key = `h:${slot.slot_number}`;
                    if (!isExpanded && extras.length === 0) updateActiveHeadlineSlot(slot.slot_number, addVariant(slot.variants));
                    setExpandedVariants(prev => ({ ...prev, [key]: !prev[key] }));
                  }}
                  title={extras.length > 0 ? `${extras.length} fallback variant(s)` : 'Add fallback variant'}
                >
                  {extras.length > 0
                    ? <span className="text-[10px] font-mono text-primary/60">+{extras.length}</span>
                    : <span className="text-[13px] opacity-30 group-hover:opacity-70">⊕</span>}
                </button>
              </div>
              {isExpanded && (
                <>
                  {extras.map((v) => (
                    <div key={v.variant_order}
                      className="grid gap-3 px-3 py-1.5 items-center bg-muted/10 border-t border-dashed border-border/40"
                      style={{ gridTemplateColumns: '36px 28px 1.5fr 1fr 72px 28px' }}>
                      <span className="text-[10px] font-mono text-muted-foreground/50">V{v.variant_order}</span>
                      <span />
                      <Input
                        value={v.template_string}
                        onChange={e => updateActiveHeadlineSlot(slot.slot_number, updateVariant(slot.variants, v.variant_order, { template_string: e.target.value }))}
                        placeholder="Fallback…"
                        className="h-7 text-sm border-0 border-b border-dashed border-border/40 rounded-none bg-transparent focus-visible:ring-0 px-0"
                        {...inputProps(`h:${slot.slot_number}:${v.variant_order}`)}
                      />
                      <div className="text-xs text-primary/60 truncate">{getPreviewText(v.template_string)}</div>
                      <CharBar {...getCharInfo(v.template_string, 30)} limit={30} />
                      <button
                        className="size-7 flex items-center justify-center rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                        onClick={() => updateActiveHeadlineSlot(slot.slot_number, removeVariant(slot.variants, v.variant_order))}
                      ><Trash2 className="size-3" /></button>
                    </div>
                  ))}
                  <div className="px-3 py-1.5 bg-muted/10 border-t border-dashed border-border/30 flex items-center justify-between">
                    <button className="text-xs font-mono text-muted-foreground/50 hover:text-primary transition-colors"
                      onClick={() => updateActiveHeadlineSlot(slot.slot_number, addVariant(slot.variants))}>
                      + add fallback
                    </button>
                    <label className="flex items-center gap-1.5 cursor-pointer" title="Leave this slot empty if no variant fits within the character limit">
                      <Checkbox
                        checked={slot.skip_if_too_long ?? false}
                        onCheckedChange={checked => updateActiveHeadlineSlotProp(slot.slot_number, { skip_if_too_long: !!checked })}
                        className="size-3.5"
                      />
                      <span className="text-[10px] text-muted-foreground/60">Skip if all variants too long</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground/50 mt-2 font-mono">{filledHeadlines}/15 set · min 3 required{isMultiAd ? ` · Ad ${activeAdIndex}` : ''}</p>
    </TabsContent>
  );
}
